import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiBriefcase,
  FiCamera,
  FiCheckCircle,
  FiDollarSign,
  FiEdit2,
  FiGrid,
  FiMoreHorizontal,
  FiPieChart,
  FiSliders,
  FiTrendingUp,
  FiAlertTriangle,
  FiArchive,
  FiFlag,
  FiHome,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUserPlus,
} from "react-icons/fi";
import { formatNaira } from "../../../utils/dashboardFormat";
import { compactNaira, buildProfileSummary } from "../../../components/MemberProfile/profileModel";
import { CopyButton, formatDay } from "../../../components/MemberProfile/parts";

import OverviewTab from "./profile-tabs/OverviewTab";
import LoansTab from "./profile-tabs/LoansTab";
import ContributionsTab from "./profile-tabs/ContributionsTab";
import SavingsTab from "./profile-tabs/SavingsTab";
import SharesTab from "./profile-tabs/SharesTab";
import RepaymentHistoryTab from "./profile-tabs/RepaymentHistoryTab";
import BankAccountTab from "./profile-tabs/BankAccountTab";
import MemberBankAccountTab from "../../Members/Profile/BankAccountTab";
import PrivacyTab from "../../Members/Profile/PrivacyTab";
import MemberExitPanel from "../../Members/Profile/MemberExitPanel";
import OpeningBalanceCorrectionModal from "../../../components/Admin/OpeningBalanceCorrectionModal";
import ImportedBalanceCorrectionsPanel from "../../Members/Profile/ImportedBalanceCorrectionsPanel";

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "M";

const Avatar = ({ photo, name }) => {
  const [broken, setBroken] = useState(false);
  const usable = photo && !broken && !String(photo).endsWith("avatar.svg");
  return (
    <div className="mp-avatar">
      {usable ? <img src={photo} alt="" onError={() => setBroken(true)} /> : <span aria-hidden="true">{initialsOf(name)}</span>}
    </div>
  );
};

const MemberAndStaffProfile = ({
  memberId,
  fullname,
  photo,
  phone,
  email,
  memberDetails,
  onEditPhoto,
  onEditProfile,
  isEditable = false,
}) => {
  const navigate = useNavigate();
  const summary = useMemo(() => buildProfileSummary(memberDetails), [memberDetails]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [openingBalanceCorrectionOpen, setOpeningBalanceCorrectionOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (e.type === "mousedown" && menuRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menuOpen]);

  const [activeTab, setActiveTab] = useState("overview");

  const tabConfig = useMemo(() => {
    const config = [
      {
        key: "overview",
        label: "Overview",
        component: (
          <OverviewTab
            memberDetails={memberDetails}
            savingsTotal={summary.savings}
            sharesTotal={summary.shares}
            onNavigate={setActiveTab}
          />
        ),
      },
      {
        key: "loans",
        label: "Loans",
        count: summary.counts.loans,
        component: (
          <LoansTab
            loans={memberDetails?.loans?.breakdown || {}}
            totalsByStatus={memberDetails?.loans?.totals_by_status || {}}
            total={summary.loanTotal}
            trustTotal={!isEditable}
          />
        ),
      },
      {
        key: "repayments",
        label: "Repayments",
        count: summary.counts.repayments,
        component: (
          <RepaymentHistoryTab
            repaymentHistory={memberDetails?.repayment_history?.breakdown || {}}
            totalsByStatus={memberDetails?.repayment_history?.totals_by_status || {}}
            memberId={memberId}
            isMemberContext={isEditable}
          />
        ),
      },
      {
        key: "savings",
        label: "Savings",
        count: summary.counts.savings,
        component: (
          <SavingsTab
            savings={memberDetails?.savings?.breakdown || []}
            total={summary.savings}
            memberId={memberId}
            isMemberContext={isEditable}
          />
        ),
      },
      {
        key: "shares",
        label: "Shares",
        count: summary.counts.shares,
        component: (
          <SharesTab
            shares={memberDetails?.shares?.breakdown || []}
            total={summary.shares}
            memberId={memberId}
            isMemberContext={isEditable}
          />
        ),
      },
      {
        key: "contributions",
        label: "Contributions",
        count: summary.counts.contributions,
        component: (
          <ContributionsTab
            contributions={memberDetails?.contributions?.breakdown || []}
            total={summary.contributions}
            memberId={memberId}
            isMemberContext={isEditable}
          />
        ),
      },
      {
        key: "bank_account",
        label: "Bank account",
        component: isEditable ? (
          // Member viewing their own profile: backend takes the member from the token
          <MemberBankAccountTab />
        ) : (
          <BankAccountTab memberId={memberDetails?.id} />
        ),
      },
    ];

    // Tabs only a member sees on their own profile
    if (isEditable) {
      config.push(
        { key: "imported_balance_corrections", label: "Imported balance corrections", component: <ImportedBalanceCorrectionsPanel /> },
        { key: "privacy", label: "Privacy", component: <PrivacyTab /> },
        { key: "membership_exit", label: "Membership exit", component: <MemberExitPanel /> }
      );
    }
    return config;
  }, [memberDetails, isEditable, memberId, summary]);

  const current = tabConfig.find((tab) => tab.key === activeTab) || tabConfig[0];
  const archived = Boolean(memberDetails?.is_archived);
  const canEdit = !isEditable && memberDetails?.id;

  const tiles = [
    { key: "savings", icon: FiDollarSign, label: "Savings", value: summary.savings, tab: "savings", tone: "green" },
    { key: "shares", icon: FiPieChart, label: "Shares", value: summary.shares, tab: "shares", tone: "purple" },
    { key: "contributions", icon: FiGrid, label: "Contributions", value: summary.contributions, tab: "contributions", tone: "blue" },
    { key: "loan", icon: FiBriefcase, label: "Active loan balance", value: summary.activeLoanBalance, tab: "loans", tone: "amber" },
    { key: "overdue", icon: FiAlertTriangle, label: "Overdue", value: summary.overdue, tab: "repayments", tone: "red", bad: summary.overdue > 0 },
    {
      key: "power",
      icon: FiTrendingUp,
      label: summary.multiplierLabel ? `Can borrow (${summary.multiplierLabel})` : "Can borrow",
      value: summary.borrowingPower,
      tab: "overview",
      hero: true,
    },
  ];

  const detail = (label, value, copy, Icon) => (
    <div>
      <dt>
        {Icon ? <Icon size={14} aria-hidden="true" /> : null}
        {label}
      </dt>
      <dd>
        <span className="mp-trunc" title={value || undefined}>{value || "—"}</span>
        {copy ? <CopyButton text={value} label={`Copy ${label.toLowerCase()}`} /> : null}
      </dd>
    </div>
  );

  const referrer = memberDetails?.referred_by;

  return (
    <section className="dx mp" aria-label="Member profile" data-tour={isEditable ? "member-profile-identity" : undefined}>
      <div className="mp-crumbs">
        <button type="button" onClick={() => navigate(-1)}>
          <FiArrowLeft size={16} aria-hidden="true" /> Back
        </button>
        <span aria-hidden="true">/</span>
        <span>{isEditable ? "My profile" : "Members"}</span>
      </div>

      <div className="mp-card mp-head">
        <div style={{ position: "relative" }}>
          <Avatar photo={photo} name={fullname} />
          {isEditable && onEditPhoto ? (
            <button
              type="button"
              className="mp-copy"
              onClick={onEditPhoto}
              aria-label="Change profile picture"
              title="Change profile picture"
              style={{ position: "absolute", right: -4, bottom: -4, background: "#fff", border: "1px solid var(--dx-line)", borderRadius: "50%", padding: 6 }}
            >
              <FiCamera size={14} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="mp-id">
          <h1 className="mp-name">{fullname || "Member"}</h1>
          <div className="mp-meta">
            <span>
              No. {memberId || "—"}
              <CopyButton text={memberId} label="Copy membership number" />
            </span>
            {memberDetails?.created_at ? <span>Joined {formatDay(memberDetails.created_at)}</span> : null}
            {memberDetails?.branch?.name ? <span>{memberDetails.branch.name}</span> : null}
            {archived ? (
              <span className="mp-pill mute">
                <FiArchive size={12} aria-hidden="true" /> Archived
              </span>
            ) : (
              <span className="mp-pill ok">
                <FiCheckCircle size={12} aria-hidden="true" /> Active
              </span>
            )}
          </div>
        </div>
        <div className="mp-actions" ref={menuRef}>
          {canEdit ? (
            <>
              <button
                type="button"
                className="mp-btn primary"
                onClick={() => navigate(`/super_admin/member_management/members/${memberDetails.id}/update`)}
              >
                <FiEdit2 size={16} aria-hidden="true" /> Edit profile
              </button>
              {!archived ? (
                <>
                  <button
                    type="button"
                    className="mp-btn"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((open) => !open)}
                  >
                    <FiMoreHorizontal size={16} aria-hidden="true" /> More
                  </button>
                  {menuOpen ? (
                    <div className="mp-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setMenuOpen(false);
                          setOpeningBalanceCorrectionOpen(true);
                        }}
                      >
                        <FiSliders size={16} aria-hidden="true" /> Correct opening balance
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}
          {isEditable && onEditProfile ? (
            <button type="button" className="mp-btn primary" onClick={onEditProfile} data-tour="member-profile-edit">
              <FiEdit2 size={16} aria-hidden="true" /> Edit profile
            </button>
          ) : null}
        </div>
      </div>

      <div className="mp-tiles" role="group" aria-label="Balances. Select one to open its records.">
        {tiles.map(({ key, icon: Icon, label, value, tab, bad, hero, tone }) => (
          <button
            key={key}
            type="button"
            className={`mp-tile tone-${hero ? "primary" : !value ? "grey" : tone} ${bad ? "bad" : ""} ${hero ? "hero" : ""} ${!hero && !value ? "zero" : ""}`}
            onClick={() => setActiveTab(tab)}
            title={value === null ? undefined : formatNaira(value)}
          >
            <span className="mp-chip-ico">
              <Icon size={16} aria-hidden="true" />
            </span>
            <span className="mp-tile-val">{value === null ? "—" : compactNaira(value)}</span>
            <span className="mp-tile-top">{label}</span>
          </button>
        ))}
      </div>

      <div className="mp-body">
        <aside className="mp-card" aria-labelledby="mp-details-h">
          <h2 className="mp-h" id="mp-details-h">Details</h2>
          <dl className="mp-dl">
            {detail("Phone", phone, true, FiPhone)}
            {detail("Email", email, true, FiMail)}
            {detail("Address", memberDetails?.address, false, FiHome)}
            {detail("State", memberDetails?.city?.name, false, FiMapPin)}
            {detail("Country", memberDetails?.country?.name, false, FiFlag)}
            {referrer?.name || referrer?.member_no
              ? detail("Referred by", `${referrer.name || "Member"}${referrer.member_no ? ` (${referrer.member_no})` : ""}`, false, FiUserPlus)
              : null}
          </dl>
        </aside>

        <div className="mp-card" data-tour={isEditable ? "member-profile-tabs" : undefined}>
          <div className="mp-tabs" role="tablist" aria-label="Member records">
            {tabConfig.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                id={`mp-tab-${tab.key}`}
                aria-selected={current.key === tab.key}
                aria-controls="mp-panel"
                className={`mp-tab ${current.key === tab.key ? "on" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tab.count > 0 ? <span className="mp-count">{tab.count}</span> : null}
              </button>
            ))}
          </div>
          <div role="tabpanel" id="mp-panel" aria-labelledby={`mp-tab-${current.key}`}>
            {current.component}
          </div>
        </div>
      </div>

      {canEdit ? (
        <OpeningBalanceCorrectionModal
          show={openingBalanceCorrectionOpen}
          onHide={() => setOpeningBalanceCorrectionOpen(false)}
          numericMemberId={memberDetails.id}
          memberLabel={`${memberDetails?.first_name ?? ""} ${memberDetails?.last_name ?? ""} (${memberDetails?.member_no ?? "—"})`}
        />
      ) : null}
    </section>
  );
};

export default MemberAndStaffProfile;
